import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Dialog, Snackbar, Alert } from '@mui/material'
import { PageLayout, PageHeader } from '../../components'
import { StatusChip, LoadingSpinner } from '../../components/atoms'
import useBillingData from '../../hooks/useBillingData'
import PaymentMethodSelector from '../../components/billing/PaymentMethodSelector'
import BillingSetupWizard from '../../components/billing/BillingSetupWizard'
import { authenticatedFetchJson } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import logger from '../../utils/logger'
import humanizeKey from '../../utils/humanizeKey'
import styles from './Billing.module.scss'

// Internal billing-runner state + redundant info — hidden from clients.
const CLIENT_HIDDEN_METADATA_KEYS = new Set([
  'source',
  'status',
  'test',
  'tax_basis_points',
  'client_name',
])

// Mirrors AdminBillingDashboard's formatter so client + admin views agree.
const formatPaymentMethodSummary = (raw) => {
  if (!raw) return null
  let pm = raw
  if (typeof raw === 'string') {
    try {
      pm = JSON.parse(raw)
    } catch (err) {
      return null
    }
  }
  if (!pm || typeof pm !== 'object' || !pm.type) return null

  if (pm.type === 'card' && pm.card) {
    const c = pm.card
    const brand = c.brand ? c.brand.charAt(0).toUpperCase() + c.brand.slice(1) : 'Card'
    const last4 = c.last4 ? `•••• ${c.last4}` : ''
    const exp =
      c.exp_month && c.exp_year
        ? ` · exp ${String(c.exp_month).padStart(2, '0')}/${String(c.exp_year).slice(-2)}`
        : ''
    return `${brand} ${last4}${exp}`.trim()
  }
  if (pm.type === 'us_bank_account' && pm.us_bank_account) {
    const b = pm.us_bank_account
    const bank = b.bank_name || 'US Bank'
    const last4 = b.last4 ? ` •••• ${b.last4}` : ''
    return `${bank}${last4}`.trim()
  }
  if (pm.type === 'acss_debit' && pm.acss_debit) {
    const b = pm.acss_debit
    const bank = b.bank_name || 'CA Bank'
    const last4 = b.last4 ? ` •••• ${b.last4}` : ''
    return `${bank}${last4}`.trim()
  }
  return pm.type.replace(/_/g, ' ')
}

const ClientInvoiceDetailsDialog = ({ open, invoice, onClose }) => {
  if (!invoice) return null
  const meta = invoice.stripe_metadata_json
  const filteredMetaEntries =
    meta && typeof meta === 'object'
      ? Object.entries(meta)
          .filter(([k]) => !CLIENT_HIDDEN_METADATA_KEYS.has(k))
          .sort(([a], [b]) => a.localeCompare(b))
      : []
  const pmLabel = formatPaymentMethodSummary(invoice.stripe_payment_method_summary_json)

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="client-invoice-details-dialog"
      PaperProps={{
        // Inherit theme bg; otherwise dark mode leaks MUI's default white.
        sx: {
          backgroundColor: 'var(--color-bg-elevated, #fff)',
          backgroundImage: 'none',
          color: 'var(--color-text-primary)',
        },
      }}
    >
      <div className={styles.detailDialog}>
        <div className={styles.detailHeader}>
          <h2 className={styles.detailTitle}>{invoice.invoice_number_local}</h2>
          <button type="button" className={styles.detailClose} onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
        <div className={styles.detailContent}>
          {invoice.stripe_description && (
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Description</span>
              <span className={styles.detailValue}>{invoice.stripe_description}</span>
            </div>
          )}
          {pmLabel && (
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Payment Method</span>
              <span className={styles.detailValue}>{pmLabel}</span>
            </div>
          )}
          {invoice.stripe_receipt_number && (
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Receipt Number</span>
              <span className={styles.detailValue}>{invoice.stripe_receipt_number}</span>
            </div>
          )}
          {invoice.stripe_statement_descriptor && (
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Statement Descriptor</span>
              <span className={styles.detailValue}>
                <code>{invoice.stripe_statement_descriptor}</code>
              </span>
            </div>
          )}
          {invoice.stripe_receipt_url && (
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>Receipt</span>
              <span className={styles.detailValue}>
                <a
                  href={invoice.stripe_receipt_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="client-receipt-link"
                >
                  View on Stripe <FontAwesomeIcon icon="external-link-alt" />
                </a>
              </span>
            </div>
          )}
          {filteredMetaEntries.length > 0 && (
            <div className={styles.detailMetadataSection}>
              <h3 className={styles.detailSectionTitle}>Charge Breakdown</h3>
              <table className={styles.detailMetadataTable}>
                <tbody>
                  {filteredMetaEntries.map(([key, value]) => (
                    <tr key={key}>
                      <td className={styles.detailMetadataKey}>{humanizeKey(key)}</td>
                      <td className={styles.detailMetadataValue}>
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </Dialog>
  )
}

ClientInvoiceDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  invoice: PropTypes.object,
  onClose: PropTypes.func.isRequired,
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const INVOICE_STATUS_COLORS = {
  draft: 'neutral',
  pending: 'warning',
  paid: 'success',
  overdue: 'error',
  cancelled: 'neutral',
  refunded: 'info',
  partially_refunded: 'info',
}

const INVOICE_STATUS_LABELS = {
  draft: 'Draft',
  pending: 'Pending',
  paid: 'Paid',
  overdue: 'Overdue',
  cancelled: 'Cancelled',
  refunded: 'Refunded',
  partially_refunded: 'Partial Refund',
}

const formatCurrency = (minorAmount, currency = 'USD') => {
  const amount = (minorAmount || 0) / 100
  return new window.Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount)
}

const formatDate = (dateStr) => {
  if (!dateStr) return '\u2014'
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

// ---------------------------------------------------------------------------
// PlanCard
// ---------------------------------------------------------------------------

const PlanCard = ({ campaignId, onPlanLoaded }) => {
  const [plan, setPlan] = useState(null)
  const [loading, setLoading] = useState(!!campaignId)

  useEffect(() => {
    if (!campaignId) return
    const fetchPlan = async () => {
      try {
        const res = await authenticatedFetchJson(
          `${apiEndpoints.billingPlans}?campaign_id=${campaignId}`,
        )
        const plans = res?.data || []
        const active = plans.find((p) => p.is_active) || null
        setPlan(active)
        if (onPlanLoaded) onPlanLoaded(active)
      } catch (err) {
        logger.error('Failed to load plan:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchPlan()
  }, [campaignId, onPlanLoaded])

  const renewalDate = useMemo(() => {
    if (!plan?.effective_date) return null
    const d = new Date(plan.effective_date)
    d.setMonth(d.getMonth() + 1)
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  }, [plan])

  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>Current Plan</h3>
      {loading ? (
        <p className={styles.emptyText}>Loading...</p>
      ) : plan ? (
        <>
          <p className={styles.planName}>{plan.plan_level_name || 'Standard'}</p>
          <div className={styles.planRow}>
            <span className={styles.planLabel}>Included minutes</span>
            <span className={styles.planValue}>
              {(plan.campaign_plan_minutes || 0).toLocaleString()}
            </span>
          </div>
          <div className={styles.planRow}>
            <span className={styles.planLabel}>Overage rate</span>
            <span className={styles.planValue}>
              {formatCurrency(plan.overage_per_minute_minor, plan.currency)}/min
            </span>
          </div>
          <div className={styles.planRow}>
            <span className={styles.planLabel}>Monthly price</span>
            <span className={styles.planValue}>
              {formatCurrency(plan.fixed_monthly_fee_minor, plan.currency)}
            </span>
          </div>
          {renewalDate && (
            <div className={styles.planRow}>
              <span className={styles.planLabel}>Renewal date</span>
              <span className={styles.planValue}>{renewalDate}</span>
            </div>
          )}
          <p className={styles.planNote}>
            To change your plan, contact your CX manager or email support@vetavirtual.com
          </p>
        </>
      ) : (
        <p className={styles.placeholderText}>No active billing plan configured.</p>
      )}
    </div>
  )
}

PlanCard.propTypes = {
  campaignId: PropTypes.number,
  onPlanLoaded: PropTypes.func,
}

// ---------------------------------------------------------------------------
// UsageCard
// ---------------------------------------------------------------------------

const UsageCard = ({ customer, plan }) => {
  const currentMinutes = customer?.current_month_minutes || 0
  const includedMinutes = plan?.campaign_plan_minutes || 0

  const pct = includedMinutes > 0 ? Math.min((currentMinutes / includedMinutes) * 100, 100) : 0
  const isOverage = currentMinutes > includedMinutes
  const overageMinutes = Math.max(0, currentMinutes - includedMinutes)

  // Days remaining in month
  const now = new Date()
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0)
  const daysLeft = Math.max(0, Math.ceil((endOfMonth - now) / (1000 * 60 * 60 * 24)))
  const totalDays = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()
  const daysPassed = totalDays - daysLeft

  // Projected end-of-month usage
  const projectedMinutes =
    daysPassed > 0 ? Math.round((currentMinutes / daysPassed) * totalDays) : 0
  const projectedOverage = Math.max(0, projectedMinutes - includedMinutes)

  const barColor = isOverage
    ? 'var(--color-status-error, #ef4444)'
    : pct > 80
      ? 'var(--color-status-warning, #f59e0b)'
      : 'var(--color-brand-primary, #e8364b)'

  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>Usage This Month</h3>
      <p className={styles.usageValue}>{Math.round(currentMinutes).toLocaleString()}</p>
      {includedMinutes > 0 ? (
        <p className={styles.usageOf}>of {includedMinutes.toLocaleString()} included minutes</p>
      ) : (
        <p className={styles.usageOf}>minutes used this month</p>
      )}

      {includedMinutes > 0 && (
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${Math.min(pct, 100)}%`, background: barColor }}
          />
        </div>
      )}

      <div className={styles.usageMeta}>
        <div className={styles.usageMetaRow}>
          <span>Days remaining</span>
          <span className={styles.usageMetaValue}>{daysLeft}</span>
        </div>
        {includedMinutes > 0 && (
          <>
            <div className={styles.usageMetaRow}>
              <span>Projected total</span>
              <span className={styles.usageMetaValue}>{projectedMinutes.toLocaleString()} min</span>
            </div>
            {isOverage ? (
              <div className={styles.usageMetaRow}>
                <span>Current overage</span>
                <span
                  className={styles.usageMetaValue}
                  style={{ color: 'var(--color-status-error)' }}
                >
                  +{Math.round(overageMinutes).toLocaleString()} min
                </span>
              </div>
            ) : projectedOverage > 0 ? (
              <div className={styles.usageMetaRow}>
                <span>Est. overage</span>
                <span
                  className={styles.usageMetaValue}
                  style={{ color: 'var(--color-status-warning)' }}
                >
                  ~{projectedOverage.toLocaleString()} min
                </span>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  )
}

UsageCard.propTypes = {
  customer: PropTypes.object,
  plan: PropTypes.object,
}

// ---------------------------------------------------------------------------
// NextInvoiceCard
// ---------------------------------------------------------------------------

const NextInvoiceCard = ({ customer, plan }) => {
  const baseFee = plan?.fixed_monthly_fee_minor || 0
  const currency = plan?.currency || customer?.billing_currency_default || 'USD'
  const currentMinutes = customer?.current_month_minutes || 0
  const includedMinutes = plan?.campaign_plan_minutes || customer?.plan_minutes || 0
  const overageRate = plan?.overage_per_minute_minor || 0
  const overageMinutes = Math.max(0, currentMinutes - includedMinutes)
  const estimatedOverage = overageMinutes * overageRate
  const total = baseFee + estimatedOverage

  return (
    <div className={styles.card}>
      <h3 className={styles.cardTitle}>Next Invoice</h3>
      {plan ? (
        <>
          <div className={styles.invoiceLineItems}>
            <div className={styles.invoiceLine}>
              <span>Base plan ({plan.plan_level_name || 'Standard'})</span>
              <span className={styles.invoiceLineValue}>{formatCurrency(baseFee, currency)}</span>
            </div>
            <div className={styles.invoiceLine}>
              <span>Estimated overage ({Math.round(overageMinutes)} min)</span>
              <span className={styles.invoiceLineValue}>
                {formatCurrency(estimatedOverage, currency)}
              </span>
            </div>
          </div>
          <hr className={styles.invoiceDivider} />
          <div className={styles.invoiceTotal}>
            <span>Estimated total</span>
            <span>{formatCurrency(total, currency)}</span>
          </div>
          <p className={styles.taxNote}>Tax is not included in the estimated total.</p>
        </>
      ) : (
        <p className={styles.placeholderText}>No plan data available to estimate next invoice.</p>
      )}
    </div>
  )
}

NextInvoiceCard.propTypes = {
  customer: PropTypes.object,
  plan: PropTypes.object,
}

// ---------------------------------------------------------------------------
// SavedMethodsCard
// ---------------------------------------------------------------------------

const SavedMethodsCard = ({ customer, bankDetails, onRefetch, onUpdateCard: _onUpdateCard }) => {
  const [openMenu, setOpenMenu] = useState(null)
  const [stripeMethods, setStripeMethods] = useState([])
  const [deleting, setDeleting] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [deleteError, setDeleteError] = useState(null)
  const menuRef = useRef(null)

  // Fetch all Stripe payment methods
  useEffect(() => {
    if (!customer?.id || !customer?.stripe_customer_id) return
    const fetchMethods = async () => {
      try {
        const res = await authenticatedFetchJson(apiEndpoints.billingPaymentMethods(customer.id))
        setStripeMethods(res?.data || [])
      } catch (err) {
        logger.error('Failed to fetch payment methods:', err)
      }
    }
    fetchMethods()
  }, [customer?.id, customer?.stripe_customer_id])

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(null)
      }
    }
    if (openMenu !== null) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [openMenu])

  const methods = []

  // Map a Stripe PaymentMethod.type to the local preferred_payment_method
  // value so the "Primary" badge resolves correctly when the user prefers PAD
  // or Stripe-ACH and not just a card.
  const STRIPE_TO_LOCAL_TYPE = {
    card: 'credit_card',
    link: 'credit_card',
    acss_debit: 'pad',
    us_bank_account: 'ach',
  }

  const summaryPm = customer?.default_payment_method_summary_json
  // A PM is "the default" when its local-mapped type matches preferred AND its
  // last4/email matches the cached default summary (Stripe's actual default).
  const isStripeDefault = (pm) => {
    if (!summaryPm || !summaryPm.type || pm.type !== summaryPm.type) return false
    if (summaryPm.last4) return pm.last4 === summaryPm.last4
    if (summaryPm.email) return pm.email === summaryPm.email
    return false
  }

  const labelForStripe = (pm) => {
    if (pm.type === 'card') {
      const brand = pm.brand ? `${pm.brand.charAt(0).toUpperCase()}${pm.brand.slice(1)}` : 'Card'
      return `${brand} \u2022\u2022\u2022\u2022 ${pm.last4}`
    }
    if (pm.type === 'link') return 'Link'
    if (pm.type === 'acss_debit') return `PAD \u2022\u2022\u2022\u2022 ${pm.last4 || ''}`
    if (pm.type === 'us_bank_account') return `ACH \u2022\u2022\u2022\u2022 ${pm.last4 || ''}`
    return `${pm.type} \u2022\u2022\u2022\u2022 ${pm.last4 || ''}`
  }

  const subForStripe = (pm) => {
    if (pm.type === 'card') return `Expires ${pm.exp_month}/${pm.exp_year}`
    if (pm.type === 'link') return pm.email || ''
    if (pm.type === 'acss_debit' || pm.type === 'us_bank_account') return pm.bank_name || ''
    return ''
  }

  const iconForStripe = (pm) => {
    if (pm.type === 'link') return 'link'
    if (pm.type === 'card') return 'credit-card'
    return 'university'
  }

  // Stripe-attached PMs: cards, Link, PAD (acss_debit), Stripe-ACH (us_bank_account)
  stripeMethods.forEach((pm) => {
    const localType = STRIPE_TO_LOCAL_TYPE[pm.type] || 'credit_card'
    methods.push({
      id: pm.id,
      icon: iconForStripe(pm),
      label: labelForStripe(pm),
      sub: subForStripe(pm),
      isDefault: customer.preferred_payment_method === localType && isStripeDefault(pm),
      type: localType,
      stripeId: pm.id,
    })
  })

  // Fall back to the cached summary while stripeMethods is still loading,
  // covering all types (card, link, PAD, Stripe-ACH). Older summaries may
  // omit `type`, so treat any record with `brand`+`last4` as a card.
  if (stripeMethods.length === 0 && summaryPm) {
    const fallbackType = STRIPE_TO_LOCAL_TYPE[summaryPm.type] || 'credit_card'
    const isCardLike =
      summaryPm.type === 'card' || (!summaryPm.type && summaryPm.brand && summaryPm.last4)
    if (isCardLike && summaryPm.last4) {
      const brand = summaryPm.brand
        ? `${summaryPm.brand.charAt(0).toUpperCase()}${summaryPm.brand.slice(1)}`
        : 'Card'
      methods.push({
        id: 'stripe-card',
        icon: 'credit-card',
        label: `${brand} \u2022\u2022\u2022\u2022 ${summaryPm.last4}`,
        sub: `Expires ${summaryPm.exp_month}/${summaryPm.exp_year}`,
        isDefault: customer.preferred_payment_method === 'credit_card',
        type: 'credit_card',
      })
    } else if (summaryPm.type === 'link') {
      methods.push({
        id: 'stripe-link',
        icon: 'link',
        label: 'Link',
        sub: summaryPm.email || '',
        isDefault: customer.preferred_payment_method === 'credit_card',
        type: 'credit_card',
      })
    } else if (summaryPm.type === 'acss_debit' && summaryPm.last4) {
      methods.push({
        id: 'stripe-pad',
        icon: 'university',
        label: `PAD \u2022\u2022\u2022\u2022 ${summaryPm.last4}`,
        sub: summaryPm.bank_name || '',
        isDefault: customer.preferred_payment_method === fallbackType,
        type: fallbackType,
      })
    } else if (summaryPm.type === 'us_bank_account' && summaryPm.last4) {
      methods.push({
        id: 'stripe-ach',
        icon: 'university',
        label: `ACH \u2022\u2022\u2022\u2022 ${summaryPm.last4}`,
        sub: summaryPm.bank_name || '',
        isDefault: customer.preferred_payment_method === fallbackType,
        type: fallbackType,
      })
    }
  }

  // Local bank_payment_details (legacy local ACH/PAD). Skip if a Stripe PM of
  // the same type already covers it to avoid duplicate rows in the list.
  if (bankDetails) {
    const bankType = bankDetails.payment_type || 'pad'
    const stripeTypeForBank = bankType === 'pad' ? 'acss_debit' : 'us_bank_account'
    const alreadyInStripe = stripeMethods.some((pm) => pm.type === stripeTypeForBank)
    if (!alreadyInStripe) {
      const bankLabel =
        bankType === 'pad'
          ? `PAD \u2022\u2022\u2022\u2022 ${bankDetails.account_number_last4 || ''}`
          : `ACH \u2022\u2022\u2022\u2022 ${bankDetails.account_number_last4 || ''}`
      const bankSub =
        bankDetails.institution_name ||
        bankDetails.bank_name ||
        bankDetails.account_holder_name ||
        ''
      methods.push({
        id: 'bank',
        icon: 'university',
        label: bankLabel,
        sub: bankSub,
        isDefault: customer.preferred_payment_method === bankType,
        type: bankType,
      })
    }
  }

  const handleSetDefault = useCallback(
    async (type) => {
      setOpenMenu(null)
      try {
        await authenticatedFetchJson(apiEndpoints.billingCustomerPreferredMethod(customer.id), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ preferred_payment_method: type }),
        })
        onRefetch()
      } catch (err) {
        logger.error('Failed to set default method:', err)
      }
    },
    [customer.id, onRefetch],
  )

  const handleRequestDelete = useCallback((m) => {
    setOpenMenu(null)
    setDeleteConfirm(m)
  }, [])

  const handleConfirmDelete = useCallback(async () => {
    const m = deleteConfirm
    if (!m) return
    setDeleteConfirm(null)
    setDeleting(m.id)
    setDeleteError(null)
    try {
      if (m.stripeId) {
        await authenticatedFetchJson(
          apiEndpoints.billingDetachPaymentMethod(customer.id, m.stripeId),
          { method: 'DELETE' },
        )
        setStripeMethods((prev) => prev.filter((pm) => pm.id !== m.stripeId))
      } else if (m.type === 'pad' || m.type === 'ach') {
        await authenticatedFetchJson(
          `${apiEndpoints.billingCustomerBankDetails(customer.id)}/${m.type}`,
          { method: 'DELETE' },
        )
      }
      onRefetch()
    } catch (err) {
      logger.error('Failed to delete method:', err)
      // Surface the server message verbatim — last_payment_method 409 carries
      // the "add another payment method first" copy the user needs.
      setDeleteError(err.message || 'Failed to delete payment method.')
    } finally {
      setDeleting(null)
    }
  }, [deleteConfirm, customer.id, onRefetch])

  return (
    <div className={styles.card}>
      <div className={styles.savedMethodsHeader}>
        <h3 className={styles.cardTitle}>Saved payment methods</h3>
        <span className={styles.methodCount}>{methods.length} on file</span>
      </div>
      {methods.length === 0 ? (
        <p className={styles.emptyMethods}>No payment methods saved yet.</p>
      ) : (
        <div className={styles.savedMethodsList}>
          {methods.map((m, idx) => (
            <div
              key={m.id}
              className={`${styles.savedMethodItem} ${m.isDefault ? styles.savedMethodDefault : ''}`}
              ref={openMenu === idx ? menuRef : null}
            >
              <div
                className={`${styles.savedMethodIcon} ${m.isDefault ? styles.savedMethodIconDefault : ''}`}
              >
                <FontAwesomeIcon icon={m.icon} />
              </div>
              <div className={styles.savedMethodInfo}>
                <div className={styles.savedMethodLabelRow}>
                  <p className={styles.savedMethodLabel}>{m.label}</p>
                  {m.isDefault && <span className={styles.defaultBadge}>Primary</span>}
                </div>
                <p className={styles.savedMethodSub}>{m.sub}</p>
              </div>
              <button
                type="button"
                className={styles.methodMenuButton}
                onClick={() => setOpenMenu(openMenu === idx ? null : idx)}
                data-testid={`method-menu-${m.id}`}
              >
                <FontAwesomeIcon icon="ellipsis-v" />
              </button>
              {openMenu === idx && (
                <div className={styles.methodMenu}>
                  {!m.isDefault && (
                    <button
                      type="button"
                      className={styles.methodMenuItem}
                      onClick={() =>
                        handleSetDefault(
                          m.type === 'card' || m.type === 'link' ? 'credit_card' : m.type,
                        )
                      }
                      data-testid={`set-default-${m.id}`}
                    >
                      <FontAwesomeIcon icon="star" /> Set as primary
                    </button>
                  )}
                  <button
                    type="button"
                    className={`${styles.methodMenuItem} ${styles.methodMenuItemDanger}`}
                    onClick={() => handleRequestDelete(m)}
                    disabled={deleting === m.id}
                    data-testid={`delete-${m.id}`}
                  >
                    <FontAwesomeIcon icon="trash-alt" />{' '}
                    {deleting === m.id ? 'Removing...' : 'Remove'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        maxWidth="xs"
        fullWidth
        data-testid="delete-method-dialog"
        className={styles.confirmDialog}
      >
        <div className={styles.confirmHeader}>
          <span className={styles.confirmTitle}>Remove Payment Method</span>
          <button
            className={styles.confirmClose}
            onClick={() => setDeleteConfirm(null)}
            aria-label="Close"
          >
            &times;
          </button>
        </div>
        <div className={styles.confirmBody}>
          {deleteConfirm && (
            <div className={styles.deleteMethodChip}>
              <FontAwesomeIcon icon="credit-card" className={styles.deleteMethodChipIcon} />
              <span>{deleteConfirm.label}</span>
            </div>
          )}
          <p className={styles.confirmText}>
            This payment method will be permanently removed from your account.
          </p>
        </div>
        <div className={styles.confirmActions}>
          <button
            type="button"
            className={styles.confirmCancelBtn}
            onClick={() => setDeleteConfirm(null)}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.confirmRemoveBtn}
            onClick={handleConfirmDelete}
            disabled={!!deleting}
          >
            Remove
          </button>
        </div>
      </Dialog>

      <Snackbar
        open={Boolean(deleteError)}
        autoHideDuration={6000}
        onClose={() => setDeleteError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {deleteError ? (
          <Alert
            onClose={() => setDeleteError(null)}
            severity="error"
            variant="filled"
            data-testid="saved-methods-error"
          >
            {deleteError}
          </Alert>
        ) : undefined}
      </Snackbar>
    </div>
  )
}

SavedMethodsCard.propTypes = {
  customer: PropTypes.object,
  bankDetails: PropTypes.object,
  onRefetch: PropTypes.func.isRequired,
  onUpdateCard: PropTypes.func,
}

// ---------------------------------------------------------------------------
// InvoiceHistorySection
// ---------------------------------------------------------------------------

const ClientSortableHeader = ({ field, label, sortField, sortDir, onSort }) => (
  <th className={styles.sortableHeader}>
    <button type="button" className={styles.sortButton} onClick={() => onSort(field)}>
      {label} {sortField === field && (sortDir === 'asc' ? '\u2191' : '\u2193')}
    </button>
  </th>
)

ClientSortableHeader.propTypes = {
  field: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  sortField: PropTypes.string,
  sortDir: PropTypes.string,
  onSort: PropTypes.func.isRequired,
}

const InvoiceHistorySection = ({ billingCustomerId }) => {
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(!!billingCustomerId)
  const [sortField, setSortField] = useState('created_at')
  const [sortDir, setSortDir] = useState('desc')
  const [selectedInvoice, setSelectedInvoice] = useState(null)

  useEffect(() => {
    if (!billingCustomerId) return
    const fetchInvoices = async () => {
      try {
        const res = await authenticatedFetchJson(
          `${apiEndpoints.billingInvoices}?billing_customer_id=${billingCustomerId}&limit=25&sort_by=${sortField}&sort_dir=${sortDir}`,
        )
        setInvoices(res?.data || [])
      } catch (err) {
        logger.error('Failed to load invoices:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchInvoices()
  }, [billingCustomerId, sortField, sortDir])

  const handleSort = useCallback(
    (field) => {
      setSortDir((prev) => (sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'))
      setSortField(field)
    },
    [sortField],
  )

  const getMethodChip = (inv) => {
    const method = inv.paid_by
    if (!method) return <span className={styles.methodChipEmpty}>{'\u2014'}</span>
    if (method === 'credit_card') {
      return (
        <span className={styles.methodChip}>
          <FontAwesomeIcon icon="credit-card" /> Credit Card
        </span>
      )
    }
    if (method === 'ach' || method === 'pad') {
      return (
        <span className={styles.methodChip}>
          <FontAwesomeIcon icon="university" /> {method.toUpperCase()}
        </span>
      )
    }
    if (method === 'eft' || method === 'wire') {
      return <span className={styles.methodChip}>{method.toUpperCase()}</span>
    }
    return <span className={styles.methodChip}>{method}</span>
  }

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Invoice History</h3>
      {loading ? (
        <p className={styles.emptyText}>Loading invoices...</p>
      ) : invoices.length === 0 ? (
        <p className={styles.placeholderText}>No invoices yet.</p>
      ) : (
        <div className={styles.invoiceTableWrapper}>
          <table className={styles.invoiceTable} data-testid="client-invoice-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Type</th>
                <ClientSortableHeader
                  field="status"
                  label="Status"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <ClientSortableHeader
                  field="total_minor"
                  label="Total"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <th>Method</th>
                <ClientSortableHeader
                  field="due_date"
                  label="Due Date"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <ClientSortableHeader
                  field="created_at"
                  label="Created"
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <th>Receipt</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id}>
                  <td className={styles.invoiceNumber}>
                    {inv.hosted_invoice_url ? (
                      <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                        {inv.invoice_number_local}
                      </a>
                    ) : (
                      <button
                        type="button"
                        className={styles.invoiceLinkButton}
                        onClick={() => setSelectedInvoice(inv)}
                        data-testid={`open-invoice-${inv.id}`}
                      >
                        {inv.invoice_number_local}
                      </button>
                    )}
                  </td>
                  <td>{inv.invoice_type}</td>
                  <td>
                    <StatusChip
                      label={INVOICE_STATUS_LABELS[inv.status] || inv.status}
                      color={INVOICE_STATUS_COLORS[inv.status] || 'neutral'}
                      size="small"
                    />
                  </td>
                  <td>{formatCurrency(inv.total_minor, inv.currency)}</td>
                  <td>{getMethodChip(inv)}</td>
                  <td>{formatDate(inv.due_date)}</td>
                  <td>{formatDate(inv.created_at)}</td>
                  <td>
                    {inv.hosted_invoice_url ? (
                      <a
                        href={inv.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.pdfLink}
                        data-testid={`pdf-link-${inv.id}`}
                      >
                        <FontAwesomeIcon icon="file-invoice-dollar" /> PDF
                      </a>
                    ) : inv.status === 'paid' ? (
                      <span className={styles.pdfLink} style={{ opacity: 0.5 }}>
                        <FontAwesomeIcon icon="file-invoice-dollar" /> PDF
                      </span>
                    ) : (
                      '\u2014'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ClientInvoiceDetailsDialog
        open={!!selectedInvoice}
        invoice={selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
      />
    </div>
  )
}

InvoiceHistorySection.propTypes = {
  billingCustomerId: PropTypes.number,
}

// ---------------------------------------------------------------------------
// StripeInvoiceHistory (Stripe-side invoices not in our local DB)
// ---------------------------------------------------------------------------

const STRIPE_INV_COLORS = {
  paid: 'success',
  open: 'warning',
  void: 'neutral',
  uncollectible: 'error',
  draft: 'neutral',
}

const formatStripeAmt = (amount, currency = 'usd') => {
  if (amount == null) return '\u2014'
  return new window.Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
    minimumFractionDigits: 2,
  }).format(amount / 100)
}

const formatStripeTs = (ts) => {
  if (!ts) return '\u2014'
  return new Date(ts * 1000).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

const StripeInvoiceHistory = ({ billingCustomerId, hasStripeCustomer }) => {
  const [invoices, setInvoices] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!billingCustomerId || !hasStripeCustomer) {
      setLoading(false)
      return
    }
    const fetch = async () => {
      try {
        const res = await authenticatedFetchJson(
          apiEndpoints.billingCustomerStripeInvoices(billingCustomerId),
        )
        setInvoices(res?.data || [])
      } catch (err) {
        logger.error('Failed to load Stripe invoices:', err)
      } finally {
        setLoading(false)
      }
    }
    fetch()
  }, [billingCustomerId, hasStripeCustomer])

  if (!hasStripeCustomer || loading) return null
  if (invoices.length === 0) return null

  return (
    <div className={styles.section}>
      <h3 className={styles.sectionTitle}>Stripe Payment History</h3>
      <p className={styles.placeholderText} style={{ marginBottom: 12 }}>
        Historical invoices from Stripe
      </p>
      <div className={styles.invoiceTableWrapper}>
        <table className={styles.invoiceTable} data-testid="stripe-invoice-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Status</th>
              <th>Amount</th>
              <th>Date</th>
              <th>Due Date</th>
              <th>Receipt</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <tr key={inv.id}>
                <td className={styles.invoiceNumber}>
                  {inv.hosted_invoice_url ? (
                    <a href={inv.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
                      {inv.number || inv.id}
                    </a>
                  ) : (
                    inv.number || inv.id
                  )}
                </td>
                <td>
                  <StatusChip
                    label={inv.status}
                    color={STRIPE_INV_COLORS[inv.status] || 'neutral'}
                    size="small"
                  />
                </td>
                <td>{formatStripeAmt(inv.amount_due, inv.currency)}</td>
                <td>{formatStripeTs(inv.created)}</td>
                <td>{formatStripeTs(inv.due_date)}</td>
                <td>
                  {inv.hosted_invoice_url ? (
                    <a
                      href={inv.hosted_invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.pdfLink}
                    >
                      <FontAwesomeIcon icon="file-invoice-dollar" /> PDF
                    </a>
                  ) : (
                    '\u2014'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

StripeInvoiceHistory.propTypes = {
  billingCustomerId: PropTypes.number,
  hasStripeCustomer: PropTypes.bool,
}

// ---------------------------------------------------------------------------
// BillingPage (main)
// ---------------------------------------------------------------------------

const BillingPage = ({
  user: _user,
  userOrganization,
  clientAccountId = null,
  adminView = false,
}) => {
  const { customer, loading, error, refetch, openPortalSession } = useBillingData(
    clientAccountId,
    clientAccountId ? null : userOrganization,
  )
  const [portalLoading, setPortalLoading] = useState(false)
  const [portalError, setPortalError] = useState(null)
  const [activePlan, setActivePlan] = useState(null)
  const [bankDetails, setBankDetails] = useState(null)
  const paymentSectionRef = useRef(null)

  // Fetch bank details for SavedMethodsCard
  useEffect(() => {
    if (!customer?.id) return
    const fetchBank = async () => {
      try {
        const res = await authenticatedFetchJson(
          apiEndpoints.billingCustomerBankDetails(customer.id),
        )
        setBankDetails(res?.data || null)
      } catch (err) {
        logger.error('Failed to fetch bank details for saved methods:', err)
        setBankDetails(null)
      }
    }
    fetchBank()
  }, [customer?.id])

  const handleManagePayment = useCallback(async () => {
    if (!customer?.id) return
    setPortalLoading(true)
    setPortalError(null)
    try {
      await openPortalSession(customer.id)
    } catch (err) {
      setPortalError('Unable to open payment portal. Please try again.')
    } finally {
      setPortalLoading(false)
    }
  }, [customer?.id, openPortalSession])

  const handleRefetch = useCallback(() => {
    refetch()
    // Re-fetch bank details too
    if (customer?.id) {
      authenticatedFetchJson(apiEndpoints.billingCustomerBankDetails(customer.id))
        .then((res) => setBankDetails(res?.data || null))
        .catch(() => setBankDetails(null))
    }
  }, [refetch, customer?.id])

  const pageTitle = adminView ? 'Client Billing View' : 'Plan & payments'
  const pageSubtitle = adminView
    ? `Viewing as admin \u2014 ${customer?.account_name || 'loading...'}`
    : 'Manage your subscription, monthly minutes, and how you pay for recurring invoices.'

  if (loading) {
    return (
      <PageLayout>
        <PageHeader title={pageTitle} subtitle="Loading..." />
        <LoadingSpinner message="Loading billing information..." />
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout>
        <PageHeader title={pageTitle} subtitle={pageSubtitle} />
        <div className={styles.errorState}>
          <p>{error}</p>
        </div>
      </PageLayout>
    )
  }

  if (!customer) {
    return (
      <PageLayout>
        <PageHeader title={pageTitle} subtitle={pageSubtitle} />
        {adminView ? (
          <div className={styles.emptyState}>
            <p>No billing profile found for this client.</p>
          </div>
        ) : (
          <BillingSetupWizard userOrganization={userOrganization} onComplete={refetch} />
        )}
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <PageHeader title={pageTitle} subtitle={pageSubtitle} />

      {/* Three info cards */}
      <div className={styles.infoCards}>
        <PlanCard campaignId={customer.campaign_id} onPlanLoaded={setActivePlan} />
        <UsageCard customer={customer} plan={activePlan} />
        <NextInvoiceCard customer={customer} plan={activePlan} />
      </div>

      {/* Payment method selector + Saved methods (3:2 grid) */}
      {!adminView && (
        <div className={styles.paymentRow}>
          <div className={styles.paymentMain} ref={paymentSectionRef}>
            <PaymentMethodSelector
              customer={customer}
              onStripePortal={handleManagePayment}
              portalLoading={portalLoading}
              onRefetch={handleRefetch}
            />
          </div>
          <div className={styles.paymentSide}>
            <SavedMethodsCard
              customer={customer}
              bankDetails={bankDetails}
              onRefetch={handleRefetch}
              onUpdateCard={() => {
                if (paymentSectionRef.current) {
                  paymentSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
              }}
            />
          </div>
        </div>
      )}
      {portalError && <p className={styles.portalError}>{portalError}</p>}

      {/* Invoice History */}
      <InvoiceHistorySection billingCustomerId={customer.id} />

      {/* Stripe Invoice History (pre-dashboard) */}
      <StripeInvoiceHistory
        billingCustomerId={customer.id}
        hasStripeCustomer={!!customer.stripe_customer_id}
      />
    </PageLayout>
  )
}

BillingPage.propTypes = {
  user: PropTypes.object,
  userOrganization: PropTypes.string,
  clientAccountId: PropTypes.number,
  adminView: PropTypes.bool,
}

export { InvoiceHistorySection }
export default BillingPage
